import { call, put } from 'redux-saga/effects';
import {
  fetchTrackingCategoriesFailure,
  fetchTrackingCategoriesSuccess,
} from '../actions';
import { ApiError } from 'store/api/types';
import { FetchTrackingCategoriesRequestAction } from '../types';
import { checkResponse } from 'store/utils';
import { GetTrackingCategoriesResponse, getTrackingCategories } from '../api';
import { TrackingCategory } from '../models';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import {
  beginTrackingCategoriesFetch,
  endTrackingCategoriesFetch,
  markTrackingCategoriesFetchFailed,
  markTrackingCategoriesFetchSucceeded,
} from 'utils/trackingCategoriesFetchGuard';

/** `data` may be `{ trackingCategories }` or nested `{ data: { trackingCategories } }` from the gateway. */
function parseTrackingCategoriesEnvelope(
  envelope: GetTrackingCategoriesResponse['data'] | undefined
): TrackingCategory[] {
  if (envelope == null) return [];
  if (Array.isArray(envelope)) return envelope as TrackingCategory[];

  const r = envelope as Record<string, unknown>;
  const tc = r.trackingCategories;
  if (Array.isArray(tc)) return tc as TrackingCategory[];

  const inner = r.data;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    const nested = (inner as Record<string, unknown>).trackingCategories;
    if (Array.isArray(nested)) return nested as TrackingCategory[];
  }
  if (Array.isArray(inner)) return inner as TrackingCategory[];

  return [];
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchTrackingCategoriesSaga(
  action: FetchTrackingCategoriesRequestAction
) {
  const { organisationId } = action.payload;
  if (!beginTrackingCategoriesFetch?.(organisationId)) return;
  try {
    let err: ApiError | null;

    const response: GetTrackingCategoriesResponse = yield call(
      getTrackingCategories,
      organisationId
    );

    err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const trackingCategories = parseTrackingCategoriesEnvelope(response.data);

    yield put(fetchTrackingCategoriesSuccess(organisationId, trackingCategories));
    markTrackingCategoriesFetchSucceeded(organisationId);
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    // Leave trackingCategories undefined so a later retry can still load them.
    markTrackingCategoriesFetchFailed(organisationId);
    yield put(fetchTrackingCategoriesFailure());
  } finally {
    endTrackingCategoriesFetch(organisationId);
  }
}
